import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Input } from '../components/common/Input';
import { Alert } from '../components/common/Alert';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../contexts/NotificationContext';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  employee_id: string;
  is_active: boolean;
  roles?: Array<{
    id: number;
    name: string;
  }>;
  departments?: Array<{
    id: number;
    name: string;
  }>;
  teams?: Array<{
    id: number;
    name: string;
  }>;
  created_at: string;
  last_login?: string;
}

export const UserManagement = () => {
  const { canManageUsers } = usePermissions();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [sortField, setSortField] = useState<string>('last_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();
  
  // Fetch departments data
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.departments || [];
    }
  });
  
  // Helper function to get department from teams
  const getDepartmentFromTeams = (userObj: User) => {
    if (!userObj?.teams || userObj.teams.length === 0) {
      return null;
    }
    
    // Find department based on team's department_id
    if (departmentsData && userObj.teams[0].id) {
      // In a real application, teams would have department_id
      // Here we're making a best effort using available data
      const teamDepartment = departmentsData.find((dept: any) => userObj.teams?.some((team) => team.id === dept.id));
      if (teamDepartment) {
        return { id: teamDepartment.id, name: teamDepartment.name };
      }
    }
    
    return null;
  };

  // Helper function to get the most relevant department for display
  const getUserDepartment = (userObj: User) => {
    // Check for departments array
    if (userObj.departments && userObj.departments.length > 0) {
      return userObj.departments[0].name;
    }
    
    // Try to find department through team membership
    const teamDept = getDepartmentFromTeams(userObj);
    if (teamDept) {
      return teamDept.name;
    }
    
    return t('userPage.noDepartment', 'No Department');
  };

  // Sorting function
  const sortUsers = (users: User[]) => {
    if (!users) return [];
    
    return [...users].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'name':
          aValue = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          bValue = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          break;
        case 'last_name':
          aValue = (a.last_name || '').toLowerCase();
          bValue = (b.last_name || '').toLowerCase();
          break;
        case 'first_name':
          aValue = (a.first_name || '').toLowerCase();
          bValue = (b.first_name || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'employee_id':
          aValue = (a.employee_id || '').toLowerCase();
          bValue = (b.employee_id || '').toLowerCase();
          break;
        case 'role':
          aValue = (a.roles?.[0]?.name || '').toLowerCase();
          bValue = (b.roles?.[0]?.name || '').toLowerCase();
          break;
        case 'department':
          aValue = getUserDepartment(a).toLowerCase();
          bValue = getUserDepartment(b).toLowerCase();
          break;
        case 'status':
          aValue = a.is_active ? 'active' : 'inactive';
          bValue = b.is_active ? 'active' : 'inactive';
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
          bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = '';
          bValue = '';
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Handle column header click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <div className="flex flex-col ml-1">
          <ChevronUpIcon className="h-3 w-3 text-gray-300" />
          <ChevronDownIcon className="h-3 w-3 text-gray-300 -mt-1" />
        </div>
      );
    }
    
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 ml-1 text-primary-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 ml-1 text-primary-600" />
    );
  };

  // Get display name for sort field
  const getSortDisplayName = (field: string) => {
    const names: Record<string, string> = {
      name: 'Imię i nazwisko',
      last_name: 'Nazwisko',
      first_name: 'Imię',
      email: 'Email',
      employee_id: 'ID Pracownika',
      role: 'Rola',
      department: 'Dział',
      status: 'Status',
      last_login: 'Ostatnie logowanie',
      created_at: 'Data utworzenia'
    };
    return names[field] || field;
  };

  // Redirect if user doesn't have permission
  if (!canManageUsers) {
    return <Navigate to="/dashboard" replace />;
  }

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users', searchTerm, selectedRole, selectedStatus],
    queryFn: async () => {
      const params: any = {
        include: 'departments,teams'
      };
      if (searchTerm) params.search = searchTerm;
      if (selectedRole !== 'all') params.role_id = selectedRole;
      if (selectedStatus !== 'all') params.is_active = selectedStatus === 'active';
      
      const response = await api.get('/users', { params });
      return response.data.users || [];
    }
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data || [];
    }
  });

  const toggleUserStatus = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      const response = await api.patch(`/users/${userId}`, { is_active: isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showNotification({
        type: 'success',
        title: t('userManagement.deleteSuccess'),
        message: t('userManagement.deleteSuccessMessage')
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('userManagement.deleteFailed'),
        message: error.response?.data?.error || t('userManagement.deleteFailedMessage')
      });
    }
  });

  const handleToggleStatus = (user: User) => {
    toggleUserStatus.mutate({ userId: user.id, isActive: !user.is_active });
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteUser.mutate(selectedUser.id);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (error) return (
    <Alert variant="error">
      {t('errors.general')}
    </Alert>
  );

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('userPage.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('userPage.description', 'Manage system users and their permissions')}
          </p>
        </div>
        <Link to="/users/new">
          <Button>
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('userPage.addUser')}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder={t('userPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="all">{t('userPage.filter.allRoles', 'Wszystkie role')}</option>
              {roles?.map((role: any) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">{t('userPage.filter.allStatus', 'Wszystkie statusy')}</option>
              <option value="active">{t('userPage.filter.active', 'Aktywny')}</option>
              <option value="inactive">{t('userPage.filter.inactive', 'Nieaktywny')}</option>
            </select>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
            >
              <option value="name">Sortuj: Imię i nazwisko</option>
              <option value="last_name">Sortuj: Nazwisko</option>
              <option value="first_name">Sortuj: Imię</option>
              <option value="email">Sortuj: Email</option>
              <option value="employee_id">Sortuj: ID Pracownika</option>
              <option value="role">Sortuj: Rola</option>
              <option value="department">Sortuj: Dział</option>
              <option value="status">Sortuj: Status</option>
              <option value="last_login">Sortuj: Ostatnie logowanie</option>
              <option value="created_at">Sortuj: Data utworzenia</option>
            </select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                className="flex items-center gap-1"
              >
                {sortDirection === 'asc' ? (
                  <>
                    <ChevronUpIcon className="h-4 w-4" />
                    Rosnąco
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-4 w-4" />
                    Malejąco
                  </>
                )}
              </Button>
            </div>
          </div>
          {sortField !== 'last_name' || sortDirection !== 'asc' ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
              <span>Sortowanie: {getSortDisplayName(sortField)} ({sortDirection === 'asc' ? 'rosnąco' : 'malejąco'})</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSortField('last_name');
                  setSortDirection('asc');
                }}
                className="text-xs"
              >
                Resetuj sortowanie
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.user', 'Użytkownik')}
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('employee_id')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.employeeId', 'ID Pracownika')}
                      <SortIcon field="employee_id" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('role')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.role', 'Rola')}
                      <SortIcon field="role" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.department', 'Dział')}
                      <SortIcon field="department" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.status', 'Status')}
                      <SortIcon field="status" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 user-select-none"
                    onClick={() => handleSort('last_login')}
                  >
                    <div className="flex items-center">
                      {t('userPage.table.lastLogin', 'Ostatnie logowanie')}
                      <SortIcon field="last_login" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userPage.table.actions', 'Akcje')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortUsers(users)?.map((user: User) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-500">
                            <span className="text-sm font-medium leading-none text-white">
                              {user.first_name?.[0] || 'U'}{user.last_name?.[0] || ''}
                            </span>
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name || ''} {user.last_name || ''}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.employee_id || t('common.placeholders.na')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="primary">
                        {user.roles?.[0]?.name || t('userPage.noRole', 'No Role')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {getUserDepartment(user)}
                        </div>
                        {user.teams && user.teams.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {user.teams.map(team => (
                              <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {team.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={user.is_active ? 'success' : 'error'}>
                        {user.is_active ? t('userPage.status.active', 'Aktywny') : t('userPage.status.inactive', 'Nieaktywny')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString()
                        : t('userPage.never', 'Never')
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Link to={`/users/${user.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(user)}
                          disabled={toggleUserStatus.isPending}
                        >
                          {user.is_active ? (
                            <XMarkIcon className="h-4 w-4 text-yellow-600" />
                          ) : (
                            <CheckIcon className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(user)}
                          disabled={deleteUser.isPending}
                        >
                          <TrashIcon className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* User count and empty state */}
      {sortUsers(users)?.length === 0 ? (
        <div className="mt-8 text-center">
          <p className="text-gray-500">{t('userManagement.noUsersFound')}</p>
        </div>
      ) : (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Wyświetlanie {sortUsers(users)?.length} użytkowników
          {(searchTerm || selectedRole !== 'all' || selectedStatus !== 'all') && (
            <span> (przefiltrowano)</span>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('userManagement.deleteDialog.title')}
        message={`${t('userManagement.deleteDialog.message')} ${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}? ${t('userManagement.deleteDialog.warning')}`}
        confirmButtonText={t('userManagement.deleteDialog.confirm')}
        variant="danger"
      />
    </div>
  );
};